// src/components/Loading/Spinner.jsx
import React, { useEffect, useLayoutEffect } from "react";
import { motion } from "../../lib/motionSafe";
import { createPortal } from "react-dom";
const SIZE_MAP = {
  xs: 18,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 52,
};
function resolvePxSize(size) {
  if (typeof size === "number") {
    return size;
  }
  if (typeof size === "string") {
    const numeric = Number.parseInt(size, 10);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
    return SIZE_MAP[size] ?? SIZE_MAP.md;
  }
  return SIZE_MAP.md;
}
function SignalSpinner({
  size = "sm",
  speed = 0.85,
  className = "",
}) {
  const px = resolvePxSize(size);
  const ringWidth = px >= 46 ? 3 : 2;
  const mask = `radial-gradient(farthest-side, transparent calc(100% - ${ringWidth}px), #000 0)`;
  return (
    <div
      className={`relative shrink-0 ${className}`}
      style={{ width: px, height: px }}
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute -inset-[22%] rounded-full bg-fuchsia-500/10 blur-xl dark:bg-fuchsia-400/10" />
      <div className="absolute inset-0 rounded-full border border-current/15" />
      <motion.div
        data-essential-motion="true"
        className="absolute inset-0 rounded-full"
        style={{
          background: `
            conic-gradient(
              from 0deg,
              transparent 0deg 214deg,
              rgba(34,211,238,0.95) 235deg,
              rgba(217,70,239,1) 286deg,
              rgba(168,85,247,1) 332deg,
              transparent 354deg
            )
          `,
          WebkitMask: mask,
          mask,
        }}
        animate={{ rotate: 360 }}
        transition={{
          duration: Math.max(0.55, speed),
          repeat: Infinity,
          ease: "linear",
        }}
      />
      <motion.div
        data-essential-motion="true"
        className="absolute left-1/2 top-1/2 h-[14%] w-[14%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"
        animate={{
          opacity: [0.35, 0.9, 0.35],
          scale: [0.75, 1, 0.75],
        }}
        transition={{
          duration: 1.35,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
}
export default function Spinner({
  size = "md",
  showLabel = false,
  label = "Ladataan…",
  className = "",
  speed = 0.85,
}) {
  return (
    <div
      className={`inline-flex flex-col items-center justify-center gap-2 text-slate-900 dark:text-white ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <SignalSpinner size={size} speed={speed} />
      {showLabel && (
        <span className="font-customFont2 text-sm font-semibold text-slate-700 dark:text-white/85">
          {label}
        </span>
      )}
    </div>
  );
}
export function PageOverlayLoading(props) {
  return <OverlayPortal {...props} />;
}
function OverlayPortal({
  label = "Ladataan…",
  spinnerSize = "lg",
  spinnerSpeed = 0.85,
  fadeInMs = 160,
  lockScroll = true,
  z = 2147483647,
}) {
  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const html = document.documentElement;
    html.classList.remove("bbx-booting");
    html.classList.add("bbx-overlay-open");
    return () => {
      html.classList.remove("bbx-overlay-open");
    };
  }, []);
  useEffect(() => {
    if (
      !lockScroll ||
      typeof document === "undefined"
    ) {
      return undefined;
    }
    const html = document.documentElement;
    const body = document.body;
    const previousOverflow = html.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - html.clientWidth;
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      html.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [lockScroll]);
  if (typeof document === "undefined") {
    return null;
  }
  const revealDuration = Math.max(
    0,
    Number(fadeInMs) || 0
  ) / 1000;
  const content = (
    <div
      className="
        fixed inset-0
        flex items-center justify-center
        overflow-hidden
        bg-[#f8f7fb]
        text-slate-950
        dark:bg-[#08050d]
        dark:text-white
      "
      style={{ zIndex: z }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        className="
          pointer-events-none absolute inset-0
          bg-[radial-gradient(circle_at_50%_48%,rgba(217,70,239,0.10),transparent_24%),radial-gradient(circle_at_58%_54%,rgba(34,211,238,0.07),transparent_28%)]
          dark:bg-[radial-gradient(circle_at_50%_48%,rgba(217,70,239,0.12),transparent_24%),radial-gradient(circle_at_58%_54%,rgba(34,211,238,0.07),transparent_28%)]
        "
        aria-hidden="true"
      />
      <div
        className="
          pointer-events-none absolute inset-x-0 top-0 h-px
          bg-gradient-to-r from-transparent via-fuchsia-400/35 to-transparent
          dark:via-fuchsia-300/25
        "
        aria-hidden="true"
      />
      <motion.div
        initial={{ opacity: 0, y: 6, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: revealDuration,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="relative z-10 flex flex-col items-center justify-center px-6 text-center"
      >
        <SignalSpinner
          size={spinnerSize}
          speed={spinnerSpeed}
          className="text-fuchsia-700 dark:text-fuchsia-200"
        />
        <div className="mt-5 font-customFont2 text-sm font-semibold tracking-[0.01em] text-slate-800 dark:text-white/90 sm:text-base">
          {label}
        </div>
      </motion.div>
    </div>
  );
  return createPortal(content, document.body);
}